import React, { useContext } from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { routes } from 'lane-shared/config';
import { UserDataContext } from 'lane-shared/contexts';
import { getDisplayName, hasPermission } from 'lane-shared/helpers';
import { PERMISSION_KEYS } from 'constants-permissions';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { LookerReportEnum } from 'constants-integrations';
import { useChannelInsightReports } from '../hooks/useChannelInsightReports';

import { AdminPage } from 'components/layout';
import { H3 } from 'components/typography';

import ChannelAdminContext from '../ChannelAdminContext';

import styles from './styles.scss';

export function ChannelInsights() {
  const { t } = useTranslation();
  const { user } = useContext(UserDataContext);
  const { channel } = useContext(ChannelAdminContext);
  const availableReports = useChannelInsightReports();

  const workOrdersAnalyticsFlag = useFlag(
    'activate-data.work-orders-analytics',
    false
  );

  const eventAnalyticsFlag = useFlag(
    FeatureFlag.EventAnalyticsLookerReport,
    false
  );

  const visitorManagementFlag = useFlag(
    FeatureFlag.VisitorManagementInsightsReports,
    false
  );

  // It's possible the user got here without having this permission
  // rather than draw graphs they can't see, just show the name
  // of the channel.
  const hasAdminPermission =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [
        PERMISSION_KEYS.PERMISSION_ADMIN,
        PERMISSION_KEYS.PERMISSION_ANALYTICS_ADVANCED,
      ],
      channel?._id
    );

  const hasWorkOrderAnalyticsPermission = hasPermission(
    user?.roles,
    [PERMISSION_KEYS.PERMISSION_ANALYTICS_WORK_ORDERS],
    channel?._id
  );

  const hasAccessAnalyticsPermission = hasPermission(
    user?.roles,
    [PERMISSION_KEYS.PERMISSION_ANALYTICS_ACCESS],
    channel?._id
  );

  const hasTenantSentimentAnalyticsPermission =
    channel?.settings.hasSurveysEnabled;

  const hasVisitorManagementPermission = hasPermission(
    user?.roles,
    [PERMISSION_KEYS.PERMISSION_ANALYTICS_VISITOR_MANAGEMENT],
    channel?._id
  );

  const hasVisitorManagementFeature =
    channel?.settings.hasVisitorManagementEnabled;

  return (
    <AdminPage className={styles.ChannelInsights}>
      {hasAdminPermission && (
        <>
          <div className={styles.header}>
            <H3>{t('web.admin.channel.insightsReports.title')}</H3>
          </div>
          <div className={styles.breakRow} />
          {Object.keys(availableReports)
            .filter(report => {
              switch (report) {
                case LookerReportEnum.WorkOrdersAnalytics:
                  return (
                    workOrdersAnalyticsFlag && hasWorkOrderAnalyticsPermission
                  );
                case LookerReportEnum.TenantSentimentAnalytics:
                  return hasTenantSentimentAnalyticsPermission;
                case LookerReportEnum.AccessAnalytics:
                  return hasAccessAnalyticsPermission;
                case LookerReportEnum.EventAnalytics:
                  return eventAnalyticsFlag;
                case LookerReportEnum.VisitorManagementAnalytics:
                  return (
                    hasVisitorManagementPermission &&
                    hasVisitorManagementFeature &&
                    visitorManagementFlag
                  );
                default:
                  return true;
              }
            })
            .map(key => (
              <Link
                key={key}
                to={routes.channelAdminInsightsReport
                  // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
                  .replace(':id', channel?.slug)
                  .replace(':selectedReport', key)}
                className={styles.reportLink}
              >
                <div>
                  {/* @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message */}
                  <H3>{t(availableReports[key].label)}</H3>
                  <p className={styles.body}>
                    {/* @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message */}
                    {t(availableReports[key].description)}
                  </p>
                </div>
              </Link>
            ))}
        </>
      )}
      {!hasAdminPermission && <h1>{getDisplayName(channel)}</h1>}
    </AdminPage>
  );
}
